import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cog, Plus, Loader2, ArrowRight } from "lucide-react";

const TASK_STATUSES = ["pending", "in_progress", "waiting", "blocked", "completed"];
const NEXT = { pending: "in_progress", in_progress: "waiting", waiting: "completed", blocked: "in_progress" };

export default function ServiceOps() {
  const qc = useQueryClient();
  const { tenant } = useAuth();
  const depts = tenant?.settings?.workflow_steps || ["Onboarding", "KYC", "Operations", "Quality Check"];
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", customer_name: "", department: depts[0], priority: "medium" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const { data: tasks = [], isLoading } = useQuery({ queryKey: ["tasks"], queryFn: async () => (await api.get("/tasks")).data });

  const createMut = useMutation({
    mutationFn: (body) => api.post("/tasks", { ...body, status: "pending", sla_status: "healthy" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tasks"] }); setOpen(false); toast.success("Task created"); },
  });
  const advanceMut = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/tasks/${id}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tasks"] }); toast.success("Task advanced & handed off"); },
  });

  return (
    <div>
      <PageHeader title="Service Operations" subtitle="Department workflows with automatic handoff" testid="service-header">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-task-btn"><Plus className="mr-1.5 h-4 w-4" /> New Task</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Create task</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Title</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="task-title-input" /></div>
              <div className="space-y-1.5"><Label>Customer</Label><Input value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="task-customer-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Department</Label>
                  <Select value={f.department} onValueChange={(v) => set("department", v)}><SelectTrigger data-testid="task-dept-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{depts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label>Priority</Label>
                  <Select value={f.priority} onValueChange={(v) => set("priority", v)}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["low", "medium", "high", "urgent"].map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <DialogFooter><Button onClick={() => createMut.mutate(f)} disabled={createMut.isPending || !f.title} data-testid="task-save-btn">{createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {isLoading ? <Loading /> : tasks.length === 0 ? (
        <EmptyState icon={Cog} title="No service tasks yet" description="Won deals automatically create service cases and tasks across departments."
          action={<Button onClick={() => setOpen(true)} className="rounded-full"><Plus className="mr-1.5 h-4 w-4" /> New Task</Button>} testid="tasks-empty" />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {depts.map((dept) => {
            const items = tasks.filter((t) => t.department === dept);
            const pending = items.filter((t) => t.status !== "completed").length;
            return (
              <Card key={dept} className="p-4" data-testid={`dept-${dept}`}>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="font-heading text-sm font-semibold">{dept}</h3>
                  <span className={`text-xs ${pending >= 3 ? "text-critical" : "text-muted-foreground"}`}>{pending} pending</span>
                </div>
                <div className="space-y-2">
                  {items.length === 0 && <p className="py-4 text-center text-xs text-muted-foreground">No tasks</p>}
                  {items.map((t) => (
                    <div key={t.id} className="rounded-lg border border-border p-3" data-testid={`task-${t.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium leading-tight">{t.title}</p>
                        <StatusBadge value={t.status} />
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">{t.customer_name}</p>
                      <div className="mt-2 flex items-center gap-2">
                        <StatusBadge value={t.priority} />
                        {t.status !== "completed" && (
                          <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" onClick={() => advanceMut.mutate({ id: t.id, status: NEXT[t.status] || "completed" })} data-testid={`advance-task-${t.id}`}>
                            Advance <ArrowRight className="ml-1 h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
